import { useMemo } from "react";

export interface SparklineProps {
  t: number[];
  v: (number | null)[];
  width?: number;
  height?: number;
  /** total flight seconds; defaults to max(t) so the sparkline spans the data */
  totalSeconds?: number;
  color?: string;
  className?: string;
  title?: string;
}

/** Tiny dependency-free SVG line for preview tiles (NaN gaps break the path). */
export function Sparkline({ t, v, width = 220, height = 44, totalSeconds, color, className, title }: SparklineProps) {
  const { d, min, max, empty } = useMemo(() => {
    let lo = Infinity;
    let hi = -Infinity;
    for (const x of v) if (x != null && Number.isFinite(x)) (lo = Math.min(lo, x)), (hi = Math.max(hi, x));
    if (!Number.isFinite(lo)) return { d: "", min: 0, max: 0, empty: true };
    if (hi === lo) (hi += 0.5), (lo -= 0.5);
    const tmax = totalSeconds && totalSeconds > 0 ? totalSeconds : Math.max(1, t[t.length - 1] ?? 1);
    const pad = 2;
    const sx = (x: number) => pad + ((width - 2 * pad) * x) / tmax;
    const sy = (y: number) => pad + (height - 2 * pad) * (1 - (y - lo) / (hi - lo));
    let path = "";
    let pen = false;
    for (let i = 0; i < t.length; i++) {
      const y = v[i];
      if (y == null || !Number.isFinite(y)) {
        pen = false;
        continue;
      }
      path += `${pen ? "L" : "M"}${sx(t[i]).toFixed(1)},${sy(y).toFixed(1)}`;
      pen = true;
    }
    return { d: path, min: lo, max: hi, empty: false };
  }, [t, v, width, height, totalSeconds]);

  return (
    <svg className={`spark ${className ?? ""}`} width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={title}>
      {title ? <title>{title}</title> : null}
      {empty ? (
        <text x={width / 2} y={height / 2 + 4} textAnchor="middle" fontSize={10} fill="currentColor" opacity={0.5}>
          no data
        </text>
      ) : (
        <>
          <path d={d} fill="none" stroke={color ?? "var(--rs-accent)"} strokeWidth={1.2} vectorEffect="non-scaling-stroke" />
          <text x={width - 2} y={9} textAnchor="end" fontSize={9} fill="currentColor" opacity={0.55}>
            {fmt(max)}
          </text>
          <text x={width - 2} y={height - 2} textAnchor="end" fontSize={9} fill="currentColor" opacity={0.55}>
            {fmt(min)}
          </text>
        </>
      )}
    </svg>
  );
}

function fmt(x: number): string {
  if (Math.abs(x) >= 1000) return x.toFixed(0);
  if (Math.abs(x) >= 10) return x.toFixed(1);
  return x.toFixed(2);
}
